'use client'

/**
 * batch-62 taken-ux (FO-464 §2.B.7) — `SwipeActie`: wikkel een kaart en swipe
 * hem naar rechts om een actie te starten (bv. "Gereedmelden"). Achter de kaart
 * verschijnt een vlak met het label; voorbij de drempel kleurt het groen en
 * start loslaten de actie. Verticaal scrollen blijft gewoon werken
 * (`touch-action: pan-y`), en een swipe telt nooit ook als tik op een knop of
 * link in de kaart.
 *
 * Een swipe is altijd een versnelling, nooit de enige weg: de kaart houdt zijn
 * eigen knop (tap-target >= 44px, ook bruikbaar met toetsenbord). De aanroeper
 * beslist wat de actie doet (direct uitvoeren met undo, of eerst een bevestiging
 * als er bv. nog checklist-punten open staan).
 */

import * as React from 'react'
import { Check } from 'lucide-react'
import { cn } from '../lib/utils'
import {
  bepaalSwipeAs,
  swipeActiveerDrempel,
  swipeActiveert,
  swipeVerschuiving,
  type SwipeAs,
} from '../lib/swipe'

export interface SwipeActieProps {
  /** Wordt aangeroepen als de gebruiker ver genoeg naar rechts veegt en loslaat. */
  onSwipe: () => void
  /** Tekst in het vlak achter de kaart, bv. "Gereedmelden". */
  label: string
  /** Zet het vegen uit (bv. terwijl een actie loopt, of als de actie niet mag). */
  disabled?: boolean
  className?: string
  children: React.ReactNode
}

export function SwipeActie({ onSwipe, label, disabled = false, className, children }: SwipeActieProps) {
  const ref = React.useRef<HTMLDivElement>(null)
  const start = React.useRef<{ x: number; y: number; id: number } | null>(null)
  const as = React.useRef<SwipeAs>('onbekend')
  const verschoven = React.useRef(false)
  const [dx, setDx] = React.useState(0)
  const [slepen, setSlepen] = React.useState(false)

  const breedte = () => ref.current?.offsetWidth ?? 0

  function reset() {
    start.current = null
    as.current = 'onbekend'
    setSlepen(false)
    setDx(0)
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (disabled) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    start.current = { x: e.clientX, y: e.clientY, id: e.pointerId }
    as.current = 'onbekend'
    verschoven.current = false
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const s = start.current
    if (!s || s.id !== e.pointerId) return
    const ddx = e.clientX - s.x
    const ddy = e.clientY - s.y
    if (as.current === 'onbekend') {
      as.current = bepaalSwipeAs(ddx, ddy)
      if (as.current === 'horizontaal') {
        verschoven.current = true
        setSlepen(true)
        try {
          ref.current?.setPointerCapture(e.pointerId)
        } catch {
          // Pointer kan al losgelaten zijn; dan volgt er vanzelf een up/cancel.
        }
      }
    }
    if (as.current === 'horizontaal') setDx(swipeVerschuiving(ddx, breedte()))
  }

  function onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    const s = start.current
    if (!s || s.id !== e.pointerId) return
    const activeert = as.current === 'horizontaal' && swipeActiveert(e.clientX - s.x, breedte())
    reset()
    if (activeert) onSwipe()
  }

  function onClickCapture(e: React.MouseEvent<HTMLDivElement>) {
    // Een swipe is geen tik: laat de knop of link onder de vinger niet óók vuren.
    if (verschoven.current) {
      e.preventDefault()
      e.stopPropagation()
      verschoven.current = false
    }
  }

  const voorbij = dx > 0 && dx >= swipeActiveerDrempel(breedte())

  return (
    <div
      ref={ref}
      className={cn('relative touch-pan-y overflow-hidden rounded-xl', className)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={reset}
      onClickCapture={onClickCapture}
    >
      {/* Vlak achter de kaart; alleen zichtbaar zolang er geveegd wordt. */}
      <div
        aria-hidden
        className={cn(
          'absolute inset-y-0 left-0 right-0 flex items-center gap-2 pl-5 text-sm font-semibold transition-colors',
          voorbij ? 'bg-streep-groen text-streep-donker' : 'bg-streep-surface-alt text-streep-text-muted',
          dx === 0 && 'invisible',
        )}
      >
        <Check className="h-5 w-5" />
        {label}
      </div>
      <div
        className={cn('relative', !slepen && 'transition-transform duration-200')}
        style={dx ? { transform: `translateX(${dx}px)` } : undefined}
      >
        {children}
      </div>
    </div>
  )
}
